// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import * as React from 'react'
import styled from 'styled-components'

import {
  Clock,
  ResponsiveImage,
  Spacer,
  Trash,
  TypeStyleBodySmall,
} from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useSiteWideMessaging } from '../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { BadgesSection } from '../../../../../components/pages/products/ProductPage/ProductHighlights/BadgesSection'
import { trackEvent } from '../../../../../lib/analytics'
import { GtmVariant } from '../../../../../lib/hooks/useGoogleTagManager'
import { CartItem } from '../../../../../types/figs'
import { CartItemQuantity } from '../../CartItemQuantity'
import { CartItemProperties } from '../CartItemProperties'
import { PriceAndDeleteButton } from '../PriceAndDeleteButton'
import { DeleteIconButton, DeleteIconColumn } from '../PriceAndDeleteButton/styles'

import {
  Body,
  Bottom,
  Details,
  DetailsColumn,
  DetailsRow,
  DiscountAppliedAtCheckoutWrap,
  FitDescription,
  ImagePlaceholder,
  ImageWrapper,
  OptionsText,
  SiteWidePromoWrap,
  Title,
  Top,
} from './styles'

export interface Props {
  cartIsUpdating: boolean
  cartItem: CartItem
  decrementQuantity(): void
  eventCategory: 'minicart' | 'cart'
  hasEmbroidery?: boolean
  incrementQuantity(): void
  isEmbroiderable?: boolean
  propertiesToDisplay: CartItem['properties']
  pushProductClick(data: { variant: GtmVariant; list?: string; position?: number }): void
  removeItem(): void
  setQuantity(newQuantity: number): void
  uniqueId: string
  /**
   * Temporary addition to support Product Bundle Discounts for the Ribbed Longsleeve Underscrub.
   * @deprecated do not use outside of temporary Product Bundle Discounts work.
   * @todo remove this after we remove this feature.
   */
  showDiscountAtCheckoutCopy: boolean
}

export const Template: React.FC<Props> = ({
  cartIsUpdating,
  cartItem: {
    isLowInventory,
    colorDisplayValue,
    effectivePrice,
    shopifyProductId,
    finalSale,
    fitDisplayValue,
    fullPrice,
    image,
    productGroupHandle,
    productType,
    properties,
    quantity,
    sizeDisplayValue,
    sku,
    swPromoEligible,
    title,
    variantId,
    urlPath,
  },
  decrementQuantity,
  eventCategory,
  incrementQuantity,
  propertiesToDisplay,
  pushProductClick,
  removeItem,
  setQuantity,
  uniqueId,
  showDiscountAtCheckoutCopy,
}) => {
  const { region } = useLocalization()
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()
  const { cart: miniCartCopy } = useTranslation()

  const linkHref = urlPath

  const { [miniCartCopy.finalSale]: finalSaleProp, ...otherPropertiesToDisplay } =
    propertiesToDisplay

  const isFinalSaleVisible = finalSale || finalSaleProp

  // Get the promotional text to display in the details section.
  // Returns "undefined" if no promotional text should be rendered.
  const promoText = React.useMemo(() => {
    if (properties['_isPortalColor'] === 'true') return undefined
    // If the product is site-wide promo eligible, use the site-wide promo text
    if (swPromoEligible && siteWidePromo) return siteWidePromo
    return undefined
  }, [properties, swPromoEligible, siteWidePromo])

  /**
   * Temporary addition to support Product Bundle Discounts for the Ribbed Longsleeve Underscrub.
   * @deprecated do not use outside of temporary Product Bundle Discounts work.
   * @todo remove this after we remove this feature.
   */
  const discountAppliedAtCheckoutText = React.useMemo(() => {
    return miniCartCopy.discountAppliedAtCheckout
  }, [miniCartCopy.discountAppliedAtCheckout])

  const translations = useTranslation()
  return (
    <Body data-testid='cart-item' data-cart-item-id={shopifyProductId}>
      <Top>
        <Link
          href={linkHref}
          onClick={() => {
            pushProductClick({
              variant: {
                category: productType,
                currencyType: region.currency,
                externalParentId: shopifyProductId,
                discountPrice: effectivePrice,
                handle: productGroupHandle,
                price: fullPrice,
                shopifyId: variantId,
                sku,
              },
              list: `${eventCategory}-cart`,
            })
          }}
          {...trackEvent({
            category: 'minicart 2.0',
            action: 'click item thumbnail',
            label: linkHref,
          })}
        >
          <ImageWrapper>
            {image?.url ? (
              <ResponsiveImage
                alt={title}
                aspectRatios={{
                  sm: 1,
                  md: 1,
                }}
                src={image.url}
                loading='lazy'
                widths={{
                  unit: 'px',
                  sm: 100,
                  md: 100,
                }}
              />
            ) : (
              <ImagePlaceholder />
            )}
          </ImageWrapper>
        </Link>

        <Details id={`Details-${uniqueId}`}>
          <DetailsRow>
            <Title asTag='h3' data-minicart-item={title}>
              {title}
            </Title>

            <DeleteIconColumn>
              <DeleteIconButton
                {...trackEvent({
                  category: eventCategory,
                  action: 'remove item from cart',
                  label: title,
                })}
                onClick={removeItem}
                aria-label={`Remove item: ${title}`}
              >
                <Trash stroke='currentColor' />
              </DeleteIconButton>
            </DeleteIconColumn>
          </DetailsRow>
          <Spacer height={4} />
          <DetailsRow $hasOffsetBelow>
            <OptionsText>
              {sizeDisplayValue}
              {productType !== 'Gift Card' && (
                <>
                  {fitDisplayValue && <FitDescription>• {fitDisplayValue}</FitDescription>}
                  &nbsp;• {colorDisplayValue}
                </>
              )}
            </OptionsText>
          </DetailsRow>
          {isFinalSaleVisible && <BadgesSection badges={[miniCartCopy.finalSale]} />}

          {!!promoText && <SiteWidePromoWrap>{promoText}</SiteWidePromoWrap>}
          {showDiscountAtCheckoutCopy ? (
            <DiscountAppliedAtCheckoutWrap>
              {discountAppliedAtCheckoutText}
            </DiscountAppliedAtCheckoutWrap>
          ) : null}
          <Spacer height={16} />
          <DetailsRow>
            <CartItemQuantity
              cartIsUpdating={cartIsUpdating}
              decrementQuantity={decrementQuantity}
              eventCategory={eventCategory}
              incrementQuantity={incrementQuantity}
              setQuantity={setQuantity}
              quantity={quantity}
            />
            <DetailsColumn>
              <PriceAndDeleteButton effectivePrice={effectivePrice} fullPrice={fullPrice} />
            </DetailsColumn>
          </DetailsRow>
        </Details>
      </Top>

      <Bottom></Bottom>
      <CartItemProperties properties={otherPropertiesToDisplay} />
      {isLowInventory && (
        <LowInventoryTypography>
          <Clock />
          {translations.pages.product.lowInventoryWarning}
        </LowInventoryTypography>
      )}
    </Body>
  )
}

const LowInventoryTypography = styled(TypeStyleBodySmall)`
  color: ${o => o.theme.color.text.caution.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  margin: ${o => o.theme.spacing(6, 0, 0, 0)};
`
